import { AgentTarget, isObjectId, normalizeTarget } from './agent-ia.models';
import type { AgentChangeAuthor, AgentDataChange, AgentNavIntent } from './agent-follow';
import type { AgentTimers } from './agent-ia.state';

// DPGF activity feed (docs/mcp/06-FRONT.md §8.2-§8.3, R12): the open DPGF page and its plans pages see what Claude, the
// « Connexions IA » and the agents of every member write in this DPGF, not only the run of the panel. Pure engine, no
// Angular import (tested with node:test); AgentFollowFeedService plugs the HTTP client, the router and the follow engine in.
// - GET /api/v1/ai/agent/v2/activity?dpgfId=&after= (back user/activity.js): every 20 s, every 5 s for 2 min after an
//   activity, only while the tab is visible and a DPGF / plans page is open; an absent route (404 without code, 501: the
//   shared demo runs another branch) stops it for good, a refused DPGF (404 NOT_FOUND, 403) or a lost session (401) until
//   the next page;
// - each change of DPGF data is published once through AgentFollower.feedChange (changes$): the page refreshes the lots
//   touched in place, or shows its banner when the user has unsaved edits — the editor is never reloaded over them;
// - the card « Claude modifie Aglo » (agent-follow-card.component) shows the latest change of someone else than the agent
//   of this panel: [Voir] for everybody, [Annuler cette modification] and [Mettre Claude en pause] for its author only;
// - ?mcpChange=<changeSetId> (link given by Claude): the rows of that change glow, its lot opens (?lotId), the parameter
//   is dropped.

export const FEED = Object.freeze({
  idleMs: 20000,       // rhythm without recent activity
  activeMs: 5000,      // rhythm for 2 min after an activity on this DPGF
  recentMs: 120000,    // « recent activity » and card lifetime
  errorMs: 60000,      // network / server error: next try
  moreMs: 300,         // next page of a long burst
  maxMore: 5,          // pages read in a row before going back to the rhythm
  undoPollMs: 1500,    // read again after an undo (its changeset_undone event)
  seenMax: 1000        // event ids kept against the overlap of two reads
});
// Pages of a DPGF that consume the feed: the editor, the plans list and the measurement editor of a plan.
const DPGF_PAGE = /^\/architect\/dpgf\/([a-f\d]{24})(?:\/plans(?:\/[a-f\d]{24})?)?$/i;
const PLAN_PAGE = /^\/architect\/dpgf\/[a-f\d]{24}\/plans(?:\/|$)/i;
const FEED_TYPE = /^(?:changeset_applied|changeset_undone|measurement_proposed|action_[a-z_]{1,30})$/;
const NAME = /^[A-Za-z]{1,40}$/;
const DATA_EVENTS = ['changeset_applied', 'changeset_undone', 'measurement_proposed'];
const ACTION_LABELS: Record<string, string> = {
  deletePost: 'propose de supprimer un poste', deleteSection: 'propose de supprimer une section', deleteLot: 'propose de supprimer un lot',
  deleteLibraryPost: 'propose de supprimer un poste de la bibliothèque', sendDce: 'propose d’envoyer le DCE', inviteCompany: 'propose d’inviter une entreprise'
};

type Raw = Record<string, any>;
const record = (value: unknown): Raw => value && typeof value === 'object' && !Array.isArray(value) ? value as Raw : {};
const clean = (value: unknown, max: number) => typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max) : '';
const pathOf = (url: string) => (url || '').split(/[?#;]/)[0].replace(/\/+$/, '') || '/';
const capitalize = (text: string) => text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

export interface FeedItem {
  id: string; type: string; at: number;            // event id, type, date (ms, server clock)
  dpgfId: string; projectId?: string;
  author: AgentChangeAuthor; clientLabel?: string;
  grantId?: string;                                // the author's own connection only (« Mettre Claude en pause »)
  changeSetId?: string; kind?: string; action?: string; actionId?: string; actionType?: string;
  target: AgentTarget;
  undoable: boolean;                               // the author can still undo it
}
export interface FeedPage { items: FeedItem[]; cursor: string; more: boolean; gap: boolean; serverTime: number; }

// One element of the route, hostile or malformed values dropped field by field; null when it cannot be used.
export function normalizeFeedItem(raw: unknown, dpgfId?: string): FeedItem | null {
  const source = record(raw), author = record(source['author']);
  const at = typeof source['at'] === 'string' ? Date.parse(source['at']) : NaN;
  if (!isObjectId(source['id']) || typeof source['type'] !== 'string' || !FEED_TYPE.test(source['type']) || !Number.isFinite(at) || !isObjectId(source['dpgfId'])) return null;
  if (dpgfId && source['dpgfId'].toLowerCase() !== dpgfId.toLowerCase()) return null;
  const mcp = source['source'] === 'mcp', mine = author['mine'] === true;
  const item: FeedItem = {
    id: source['id'], type: source['type'], at, dpgfId: source['dpgfId'],
    author: { source: mcp ? 'mcp' : 'agent', tag: !mcp ? 'Agent IA' : source['authorTag'] === 'Connexion IA' ? 'Connexion IA' : 'Claude', name: clean(author['name'], 120), mine },
    target: normalizeTarget(source['target']), undoable: false
  };
  if (isObjectId(source['projectId'])) item.projectId = source['projectId'];
  const label = clean(source['clientLabel'], 80);
  if (mcp && label) item.clientLabel = label;
  if (mine && mcp && isObjectId(source['grantId'])) item.grantId = source['grantId'];
  for (const key of ['changeSetId', 'actionId'] as const) if (isObjectId(source[key])) item[key] = source[key];
  for (const key of ['kind', 'action', 'actionType'] as const) if (typeof source[key] === 'string' && NAME.test(source[key])) item[key] = source[key];
  item.undoable = mine && source['undoable'] === true && Boolean(item.changeSetId);
  return item;
}
export function normalizeFeedPage(raw: unknown, dpgfId: string): FeedPage | null {
  const source = record(raw);
  if (!Array.isArray(source['items']) || typeof source['cursor'] !== 'string' || !/^\d{1,15}(?:-[a-f\d]{24})?$/i.test(source['cursor'])) return null;
  const serverTime = typeof source['serverTime'] === 'string' ? Date.parse(source['serverTime']) : NaN;
  return {
    items: source['items'].slice(0, 500).map(item => normalizeFeedItem(item, dpgfId)).filter((item): item is FeedItem => item !== null),
    cursor: source['cursor'], more: source['more'] === true, gap: source['gap'] === true, serverTime: Number.isFinite(serverTime) ? serverTime : NaN
  };
}

// AgentDataChange of an element that changed DPGF data (lots, posts, plans, measurements); null otherwise (project,
// library, proposals: nothing to refresh in the DPGF pages).
export function feedDataChange(item: FeedItem): AgentDataChange | null {
  if (!item || !DATA_EVENTS.includes(item.type)) return null;
  const kind = item.type === 'measurement_proposed' ? 'plan' : item.kind;
  if (kind !== 'dpgf' && kind !== 'plan') return null;
  const target = item.target;
  return {
    seq: 0, runId: '', changeSetId: item.changeSetId || '', kind, ...(item.action ? { action: item.action } : {}), undone: item.type === 'changeset_undone',
    dpgfId: item.dpgfId, lotIds: target.lotIds ? [...target.lotIds] : null, removedLotIds: target.removedLotIds || [], postIds: target.postIds || [],
    ops: target.ops || [], ...(target.planId ? { planId: target.planId } : {}), planIds: target.planIds || [], author: { ...item.author }
  };
}
// Everything may have changed (the tab missed more than the route keeps): the pages read the whole DPGF again.
export function fullRefresh(dpgfId: string): AgentDataChange {
  return { seq: 0, runId: '', changeSetId: '', kind: 'dpgf', undone: false, dpgfId, lotIds: null, removedLotIds: [], postIds: [], ops: [], planIds: [] };
}

// ---- Texts ---------------------------------------------------------------------------------

// « Claude », « Connexion IA », « L’Agent IA » for the user's own; « Claude (Marc Membre) » for a colleague's (U14, U17).
export function authorLabel(author: AgentChangeAuthor | null | undefined): string {
  if (!author) return 'L’Agent IA';
  const base = author.tag === 'Agent IA' ? 'L’Agent IA' : author.tag;
  return author.mine ? base : `${base} (${author.name || 'un collègue'})`;
}
// What the element did, as the card's live line (« A modifié le lot « Gros œuvre » »). Never a text of the change.
export function feedSummary(item: FeedItem): string {
  const target = item.target, lots = target.lots || [], lotIds = target.lotIds;
  switch (item.type) {
    case 'changeset_undone': return 'Modification annulée';
    case 'measurement_proposed': return 'A proposé des métrés à vérifier';
    case 'action_proposed': return capitalize((item.actionType && ACTION_LABELS[item.actionType]) || 'propose une action à valider');
    case 'action_executed': return 'Action validée et exécutée';
    case 'action_rejected': return 'Action refusée';
    case 'action_failed': return 'Action non aboutie';
    case 'changeset_applied': break;
    default: return 'Action mise à jour';
  }
  if (item.kind === 'plan') return item.action === 'attach' ? 'A ajouté des plans' : item.action === 'reject' ? 'A retiré des métrés IA' : 'A modifié un plan';
  if (item.kind !== 'dpgf') return item.kind === 'library' ? 'A modifié la bibliothèque' : 'A modifié le projet';
  const removed = (target.removedLotIds || []).length;
  if (removed && !(lotIds || []).length) return removed > 1 ? `A supprimé ${removed} lots` : 'A supprimé un lot';
  if (lotIds && lotIds.length === 1) {
    const title = lots.find(lot => lot.id === lotIds[0])?.title || '';
    return title ? `A modifié le lot « ${title} »` : 'A modifié un lot';
  }
  if (lotIds && lotIds.length > 1) return `A modifié ${lotIds.length} lots`;
  return 'A modifié la DPGF';
}
// « à l’instant », « il y a 40 s », « il y a 3 min ».
export function relativeTime(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 10) return 'à l’instant';
  return seconds < 60 ? `il y a ${seconds} s` : `il y a ${Math.floor(seconds / 60)} min`;
}
export function feedDpgfOfUrl(url: string): string {
  const match = DPGF_PAGE.exec(pathOf(url));
  return match ? match[1].toLowerCase() : '';
}
export function mcpChangeOfUrl(url: string): string {
  const query = (url || '').split('#')[0].split('?')[1] || '';
  for (const part of query.split('&')) {
    const [key, value = ''] = part.split('=');
    if (key === 'mcpChange') { const id = decodeURIComponent(value); return isObjectId(id) ? id.toLowerCase() : ''; }
  }
  return '';
}
// The lot to open for an element: its only lot, otherwise the most touched one with a title.
function lotOf(target: AgentTarget): string {
  if (target.lotIds && target.lotIds.length === 1) return target.lotIds[0];
  return target.lots?.[0]?.id || target.lotIds?.[0] || '';
}
// Where « Voir » leads: the lot (or the DPGF), the plan, or the approval page of the author's proposal.
export function feedIntent(item: FeedItem): AgentNavIntent | null {
  if (item.type === 'measurement_proposed' || item.kind === 'plan') {
    const planId = item.target.planId || (item.target.planIds?.length === 1 ? item.target.planIds[0] : '');
    return planId ? { kind: 'plan', dpgfId: item.dpgfId, planId, label: 'le plan' } : { kind: 'plans', dpgfId: item.dpgfId, label: 'les plans' };
  }
  // Undone, proposals, project and library changes: nothing of this DPGF to show.
  if (item.kind !== 'dpgf' || item.type !== 'changeset_applied') return null;
  const lotId = (item.target.removedLotIds || []).length && !(item.target.lotIds || []).length ? '' : lotOf(item.target);
  const title = lotId ? item.target.lots?.find(lot => lot.id === lotId)?.title : '';
  return lotId ? { kind: 'dpgf', dpgfId: item.dpgfId, lotId, label: title ? `le lot ${title}` : 'le lot modifié' } : { kind: 'dpgf', dpgfId: item.dpgfId, label: 'la DPGF' };
}

export function undoErrorMessage(error: unknown): string {
  const { status, code } = apiError(error);
  if (code === 'ALREADY_UNDONE') return 'Cette modification est déjà annulée.';
  if (status === 409 || code === 'CONFLICT') return 'Ces éléments ont été modifiés depuis : cette modification ne peut plus être annulée ici.';
  if (status === 404) return 'Cette modification est introuvable.';
  return 'L’annulation n’a pas abouti. Réessayez dans un instant.';
}
// Angular HttpErrorResponse: { status, error: { error: { code, message } } }.
export function apiError(error: unknown): { status: number; code: string } {
  const source = record(error), body = record(record(source['error'])['error']);
  return { status: typeof source['status'] === 'number' ? source['status'] : 0, code: typeof body['code'] === 'string' ? body['code'] : '' };
}
const missingRoute = (error: unknown) => { const { status, code } = apiError(error); return status === 501 || (status === 404 && code !== 'NOT_FOUND'); };

// ---- Engine --------------------------------------------------------------------------------

export interface FeedApi {
  feed(dpgfId: string, cursor: string | null): Promise<unknown>;     // GET /activity?dpgfId=&after=
  change(dpgfId: string, changeSetId: string): Promise<unknown>;     // GET /activity?dpgfId=&changeSetId=
  undo(changeSetId: string): Promise<unknown>;                       // POST /changesets/:id/undo (the panel's route)
  pause(grantId: string): Promise<unknown>;                          // PATCH /api/v1/mcp/connections/:id {paused:true}
}
export interface FeedEnv {
  api: FeedApi;
  now(): number; timers: AgentTimers; visible(): boolean; url(): string;
  publish(change: AgentDataChange): boolean;                         // AgentFollower.feedChange
  highlight(dpgfId: string, lotIds: string[] | null, postIds: string[]): void;
  isShown(intent: AgentNavIntent): boolean;
  pageBlock(): { reason: string; canSave: boolean };
  open(intent: AgentNavIntent): Promise<boolean>;                    // AgentFollower.open: saves the edits first, or stays
  flush(): Promise<boolean>;                                         // AgentFollower.flushPages
  go(commands: string[]): void;                                      // approval page of a proposal
  replaceQuery(params: Record<string, string | null>): void;         // ?lotId / ?mcpChange of the current page
  changed(): void;                                                   // the card's view
}
export interface FeedCardView {
  title: string; who: string; summary: string;
  viewLabel: string;           // '' = no « Voir »
  canUndo: boolean; canPause: boolean;
  pauseLabel: string;          // « Mettre Claude en pause », « Mettre la connexion en pause »
}

export class DpgfActivityFeed {
  available = true;            // false once the route is absent: nothing is read any more
  dpgfId = '';                 // DPGF of the page followed ('' = none)
  card: FeedItem | null = null;
  message = '';
  busy: '' | 'undo' | 'pause' | 'view' = '';
  paused = '';                 // grantId put in pause from the card

  private cursor: string | null = null;
  private timer: unknown = null;
  private inFlight = false;
  private generation = 0;      // a read answered for another page is ignored
  private blocked = '';        // DPGF refused (403, 404 NOT_FOUND) or session lost: not read again before the next page
  private lastRead = -Infinity;
  private lastActivity = -Infinity;   // server clock
  private offset = 0;                 // server clock - local clock
  private burst = 0;
  private seen = new Set<string>();
  private dismissed = '';
  private handledChange = '';
  private disposed = false;

  constructor(private env: FeedEnv) {}

  // Route changed, tab shown or hidden: follow the DPGF of the page, read when due.
  sync() {
    if (this.disposed) return;
    const dpgfId = this.available ? feedDpgfOfUrl(this.env.url()) : '';
    if (dpgfId !== this.dpgfId) this.reset(dpgfId);
    const changeSetId = dpgfId ? mcpChangeOfUrl(this.env.url()) : '';
    if (changeSetId && changeSetId !== this.handledChange) { this.handledChange = changeSetId; void this.followChange(dpgfId, changeSetId); }
    this.plan();
  }
  dispose() { this.disposed = true; this.clearTimer(); this.generation++; }

  serverNow() { return this.env.now() + this.offset; }
  // Rhythm: 5 s for 2 min after an activity, otherwise 20 s.
  interval() { return this.serverNow() - this.lastActivity < FEED.recentMs ? FEED.activeMs : FEED.idleMs; }

  private reset(dpgfId: string) {
    this.clearTimer(); this.generation++;
    this.dpgfId = dpgfId; this.cursor = null; this.blocked = ''; this.lastRead = -Infinity; this.lastActivity = -Infinity; this.burst = 0;
    this.seen.clear(); this.card = null; this.message = ''; this.handledChange = ''; this.inFlight = false;
    this.env.changed();
  }
  private plan(delay?: number) {
    this.clearTimer();
    if (this.disposed || !this.available || !this.dpgfId || this.blocked === this.dpgfId || this.inFlight || !this.env.visible()) return;
    const wait = delay ?? Math.max(0, this.lastRead + this.interval() - this.env.now());
    this.timer = this.env.timers.setTimeout(() => { this.timer = null; void this.read(); }, wait);
  }
  private clearTimer() { if (this.timer !== null) this.env.timers.clearTimeout(this.timer); this.timer = null; }

  async read(): Promise<void> {
    if (this.disposed || this.inFlight || !this.available || !this.dpgfId || this.blocked === this.dpgfId || !this.env.visible()) return;
    const dpgfId = this.dpgfId, generation = this.generation;
    this.inFlight = true;
    let next: number | undefined;
    try {
      const page = normalizeFeedPage(await this.env.api.feed(dpgfId, this.cursor), dpgfId);
      if (generation !== this.generation) return;
      if (!page) throw new Error('Réponse du flux illisible');
      if (Number.isFinite(page.serverTime)) this.offset = page.serverTime - this.env.now();
      if (page.gap) this.env.publish(fullRefresh(dpgfId));
      for (const item of page.items) this.take(item);
      this.cursor = page.cursor;
      this.burst = page.more ? this.burst + 1 : 0;
      if (page.more && this.burst <= FEED.maxMore) next = FEED.moreMs;
    } catch (error) {
      if (generation !== this.generation) return;
      if (missingRoute(error)) { this.available = false; this.card = null; }
      else if ([401, 403, 404].includes(apiError(error).status)) this.blocked = dpgfId;
      else next = FEED.errorMs;
    } finally {
      if (generation === this.generation) { this.inFlight = false; this.lastRead = this.env.now(); }
    }
    this.env.changed();
    this.plan(next);
  }
  private take(item: FeedItem) {
    if (this.seen.has(item.id)) return;
    if (this.seen.size >= FEED.seenMax) this.seen.clear();   // the overlap of two reads is a few seconds: old ids are useless
    this.seen.add(item.id);
    this.lastActivity = Math.max(this.lastActivity, item.at);
    const change = feedDataChange(item);
    if (change) this.env.publish(change);
    // A later undo of the change shown (from Aglo, from Claude): it can no longer be undone.
    if (item.type === 'changeset_undone' && this.card && item.changeSetId && this.card.changeSetId === item.changeSetId) this.card.undoable = false;
    // The agent of this panel has its own bubble (agent-activity-bubble): its card would show the same thing twice.
    if (item.author.source === 'agent' && item.author.mine) return;
    if (!this.card || item.at >= this.card.at) { if (this.card?.id !== item.id) this.message = ''; this.card = item; }
  }

  // ---- Card ------------------------------------------------------------------------------

  cardView(): FeedCardView | null {
    const item = this.card;
    if (!this.available || !item || item.id === this.dismissed || item.dpgfId !== this.dpgfId || this.serverNow() - item.at >= FEED.recentMs) return null;
    const mine = item.author.mine, when = relativeTime(this.serverNow() - item.at);
    const intent = feedIntent(item);
    const block = intent && !this.env.isShown(intent) ? this.env.pageBlock() : { reason: '', canSave: false };
    const approval = mine && item.type === 'action_proposed' && Boolean(item.actionId);
    return {
      title: `${item.author.tag === 'Agent IA' ? 'L’Agent IA' : item.author.tag} modifie Aglo`,
      who: mine ? when : `${item.author.name || 'Un collègue'} · ${when}`,
      summary: feedSummary(item),
      viewLabel: approval ? 'Voir l’action' : intent ? (block.canSave ? 'Enregistrer et voir' : 'Voir') : '',
      canUndo: mine && item.undoable && Boolean(item.changeSetId),
      canPause: mine && item.author.source === 'mcp' && Boolean(item.grantId) && this.paused !== item.grantId,
      pauseLabel: item.author.tag === 'Claude' ? 'Mettre Claude en pause' : 'Mettre la connexion en pause'
    };
  }
  dismiss() { if (this.card) this.dismissed = this.card.id; this.message = ''; this.env.changed(); }

  // « Voir »: the rows glow and the lot (or plan) opens; the pending edits are saved first by the follow engine (or the
  // page stays, with its reason). The approval page for the author's proposal.
  async view(): Promise<void> {
    const item = this.card;
    if (!item || this.busy || this.disposed) return;
    if (item.author.mine && item.type === 'action_proposed' && item.actionId) { this.env.go(['/architect/connexions-ia/valider', item.actionId]); return; }
    const intent = feedIntent(item);
    if (!intent) return;
    if (item.kind === 'dpgf' && item.type === 'changeset_applied') this.env.highlight(item.dpgfId, item.target.lotIds ? [...item.target.lotIds] : null, item.target.postIds || []);
    // Already displayed: ?lotId again makes the page highlight the rows (lotParamDecision 'shown').
    if (this.env.isShown(intent)) { if (intent.lotId) this.env.replaceQuery({ lotId: intent.lotId }); return; }
    this.busy = 'view'; this.message = ''; this.env.changed();
    try {
      const reason = this.env.pageBlock().reason;
      const done = await this.env.open(intent);
      if (!done && reason) this.message = `${reason.replace(/[\s.!…]+$/, '')} : la page reste ouverte.`;
    } catch { this.message = 'La page n’a pas pu être ouverte.'; }
    finally { this.busy = ''; this.env.changed(); }
  }
  // « Annuler cette modification » (its author only): the pages save their edits first, as for an undo from the panel.
  async undo(): Promise<void> {
    const item = this.card;
    if (!item || !item.author.mine || !item.undoable || !item.changeSetId || this.busy || this.disposed) return;
    this.busy = 'undo'; this.message = ''; this.env.changed();
    try {
      if (!(await this.env.flush())) { this.message = 'Vos modifications n’ont pas pu être enregistrées : rien n’a été annulé.'; return; }
      await this.env.api.undo(item.changeSetId);
      item.undoable = false;
      this.message = 'Modification annulée.';
      if (item.id === this.card?.id) this.plan(FEED.undoPollMs);
    } catch (error) { this.message = undoErrorMessage(error); }
    finally { this.busy = ''; this.env.changed(); }
  }
  // « Mettre Claude en pause » (its author only): the connection can no longer write until it is resumed in the tab.
  async pause(): Promise<void> {
    const item = this.card;
    if (!item || !item.author.mine || item.author.source !== 'mcp' || !item.grantId || this.busy || this.disposed) return;
    this.busy = 'pause'; this.message = ''; this.env.changed();
    try {
      await this.env.api.pause(item.grantId);
      this.paused = item.grantId;
      this.message = `${item.author.tag === 'Claude' ? 'Claude est en pause' : 'La connexion est en pause'} : plus aucune modification possible. Reprise dans Paramètres › Connexions IA.`;
    } catch { this.message = 'La mise en pause n’a pas abouti. Réessayez dans un instant.'; }
    finally { this.busy = ''; this.env.changed(); }
  }

  // ---- ?mcpChange=<changeSetId> ------------------------------------------------------------

  private async followChange(dpgfId: string, changeSetId: string): Promise<void> {
    const generation = this.generation, plans = PLAN_PAGE.test(pathOf(this.env.url()));
    const params: Record<string, string | null> = { mcpChange: null };
    try {
      if (!plans) {
        const answer = record(await this.env.api.change(dpgfId, changeSetId));
        if (generation !== this.generation) return;
        const item = normalizeFeedItem(answer['change'], dpgfId);
        if (item && answer['status'] === 'undone') this.message = 'Cette modification a été annulée depuis.';
        else if (item && item.kind === 'dpgf' && item.changeSetId === changeSetId) {
          this.env.highlight(dpgfId, item.target.lotIds ? [...item.target.lotIds] : null, item.target.postIds || []);
          const lotId = lotOf(item.target);
          if (lotId) params['lotId'] = lotId;
        }
      }
    } catch (error) {
      if (generation !== this.generation) return;
      if (missingRoute(error)) this.available = false;
    }
    this.env.replaceQuery(params);
    this.env.changed();
  }
}
